<template>
  <section
    v-if="visible"
    class="login-welcome-overlay"
    role="status"
    aria-live="polite"
    aria-atomic="true"
    aria-busy="true"
  >
    <div class="login-welcome-overlay__content">
      <div class="login-welcome-overlay__logo-wrap">
        <span class="login-welcome-overlay__ring" aria-hidden="true"></span>
        <img
          class="login-welcome-overlay__logo"
          src="../assets/logo-suite241.png"
          alt="Suite241"
        />
      </div>

      <h1>Bienvenue, {{ welcomeName }} !</h1>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {
  consumeLoginWelcomePending,
  type LoginWelcomeUserId,
} from '@/support/loginWelcome';

interface WelcomeUser {
  id?: LoginWelcomeUserId;
  name?: string | null;
  username?: string | null;
}

interface LoginWelcomeOverlayData {
  visible: boolean;
  hideTimer: ReturnType<typeof setTimeout> | null;
}

const DISPLAY_DURATION_MS = 5000;

export default defineComponent({
  name: 'AppLoginWelcomeOverlay',
  data(): LoginWelcomeOverlayData {
    return {
      visible: false,
      hideTimer: null,
    };
  },
  computed: {
    currentUser(): WelcomeUser | null {
      return this.$store.getters['auth/currentUser'] || null;
    },
    currentUserId(): LoginWelcomeUserId {
      return this.currentUser?.id ?? null;
    },
    welcomeName(): string {
      const fullName = String(this.currentUser?.name || '').trim();
      const username = String(this.currentUser?.username || '').trim();

      return fullName || username || 'Utilisateur';
    },
  },
  watch: {
    currentUserId: {
      immediate: true,
      handler(userId: LoginWelcomeUserId): void {
        if (!consumeLoginWelcomePending(userId)) return;

        this.showWelcome();
      },
    },
  },
  beforeUnmount() {
    this.stopHideTimer();
  },
  methods: {
    showWelcome(): void {
      this.stopHideTimer();
      this.visible = true;
      this.hideTimer = setTimeout(() => {
        this.visible = false;
        this.hideTimer = null;
      }, DISPLAY_DURATION_MS);
    },
    stopHideTimer(): void {
      if (this.hideTimer === null) return;

      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    },
  },
});
</script>

<style scoped>
.login-welcome-overlay {
  align-items: center;
  background:
    radial-gradient(circle at 50% 38%, rgba(255, 255, 255, 0.98), rgba(246, 250, 255, 0.96) 38%, rgba(229, 239, 250, 0.98)),
    #f4f8fc;
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1.5rem;
  position: fixed;
  z-index: 4000;
}

.login-welcome-overlay__content {
  align-items: center;
  display: flex;
  flex-direction: column;
  text-align: center;
}

.login-welcome-overlay__logo-wrap {
  align-items: center;
  display: flex;
  height: clamp(8.5rem, 26vw, 12rem);
  justify-content: center;
  position: relative;
  width: clamp(8.5rem, 26vw, 12rem);
}

.login-welcome-overlay__ring {
  animation: login-welcome-ring-spin 1.1s linear infinite;
  border: 0.35rem solid rgba(20, 86, 145, 0.16);
  border-radius: 50%;
  border-top-color: #145691;
  height: 100%;
  inset: 0;
  position: absolute;
  width: 100%;
}

.login-welcome-overlay__logo {
  height: 62%;
  object-fit: contain;
  position: relative;
  width: 62%;
}

.login-welcome-overlay h1 {
  color: #15324b;
  font-size: clamp(1.35rem, 4vw, 2rem);
  font-weight: 800;
  margin: 1.75rem 0 0;
}

@keyframes login-welcome-ring-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .login-welcome-overlay__ring {
    animation: none;
  }
}
</style>
